export interface ProjectVersionDto {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  startDate?: string;
  releaseDate?: string;
  releaseNotes?: string;
  status: number;
  statusLabel: string;
  sortOrder: number;
  issueCount: number;
  progressPercent?: number;
  completedIssueCount?: number;
  deploymentStatus?: number;
  deploymentStatusLabel?: string;
  approvalStatus?: number;
  approvalStatusLabel?: string;
  approvedById?: string;
  approvedAt?: string;
  checklistTotal?: number;
  checklistDone?: number;
  completionPercent?: number;
  deployedAt?: string;
  deployedEnvironment?: string;
}

export interface ReleaseChecklistItemDto {
  id: string;
  versionId: string;
  title: string;
  isDone: boolean;
  sortOrder: number;
}

export interface ReleaseCalendarEntryDto {
  id: string;
  versionId: string;
  name: string;
  releaseDate?: string;
  startDate?: string;
  status: number;
  statusLabel?: string;
  deploymentStatus?: number;
  deploymentStatusLabel?: string;
  releaseNotes?: string;
}

export interface ReleaseCalendarDto {
  projectId: string;
  from?: string;
  to?: string;
  entries: ReleaseCalendarEntryDto[];
}

export interface DeployVersionRequest {
  deploymentStatus?: number;
  environment?: string;
  releaseNotes?: string;
  notes?: string;
}

export interface IssueCustomFieldValueDto {
  fieldDefinitionId: string;
  fieldName: string;
  fieldKey: string;
  fieldType: number;
  valueText?: string;
  valueNumber?: number;
  valueDate?: string;
  valueJson?: string;
  displayValue?: string;
}

export interface SetIssueCustomFieldValueRequest {
  fieldDefinitionId: string;
  valueText?: string;
  valueNumber?: number;
  valueDate?: string;
  valueJson?: string;
}

export interface ProjectMilestoneDto {
  id: string;
  projectId: string;
  versionId?: string;
  versionName?: string;
  name: string;
  description?: string;
  targetDate?: string;
  status: number;
  statusLabel?: string;
  sortOrder: number;
}

export interface RoadmapVersionDto extends ProjectVersionDto {
  milestones?: ProjectMilestoneDto[];
  deploymentStatus?: number;
  deploymentStatusLabel?: string;
}

export interface RoadmapDto {
  projectId: string;
  versions: RoadmapVersionDto[];
  milestones: ProjectMilestoneDto[];
}

export interface IssueVersionLinkDto {
  versionId: string;
  name: string;
  relation: number;
  relationLabel: string;
  status: number;
}

export interface CustomFieldDefinitionDto {
  id: string;
  projectId?: string;
  name: string;
  key: string;
  description?: string;
  fieldType: number;
  fieldTypeLabel: string;
  optionsJson?: string;
  isRequired: boolean;
  isActive: boolean;
  sortOrder: number;
}

export interface SavedIssueFilterDto {
  id: string;
  ownerUserId: string;
  ownerName: string;
  projectId?: string;
  name: string;
  description?: string;
  jql: string;
  isShared: boolean;
  createdAt: string;
}

export interface IssueSearchHit {
  id: string;
  key: string;
  summary: string;
  typeLabel: string;
  statusLabel: string;
  priorityLabel: string;
  assigneeName?: string;
  projectName: string;
}

export const MILESTONE_STATUS_OPTIONS = [
  { value: 0, label: 'Pending' },
  { value: 1, label: 'In progress' },
  { value: 2, label: 'Completed' },
  { value: 3, label: 'Cancelled' }
];

export const VERSION_STATUS_OPTIONS = [
  { value: 0, label: 'Unreleased' },
  { value: 1, label: 'Released' },
  { value: 2, label: 'Archived' }
];

export const DEPLOYMENT_STATUS_OPTIONS = [
  { value: 0, label: 'Not deployed' },
  { value: 1, label: 'Staging' },
  { value: 2, label: 'Production' },
  { value: 3, label: 'Rolled back' }
];

export const CUSTOM_FIELD_TYPE_OPTIONS = [
  { value: 0, label: 'Text' },
  { value: 1, label: 'Number' },
  { value: 2, label: 'Date' },
  { value: 3, label: 'Select' },
  { value: 4, label: 'MultiSelect' },
  { value: 5, label: 'Checkbox' },
  { value: 6, label: 'Url' }
];

export type TimelineZoom = 'Week' | 'Month' | 'Quarter' | 'Year';

export const TIMELINE_ZOOM_OPTIONS: { value: TimelineZoom; label: string }[] = [
  { value: 'Week', label: 'Week' },
  { value: 'Month', label: 'Month' },
  { value: 'Quarter', label: 'Quarter' },
  { value: 'Year', label: 'Year' }
];

export interface TimelineItemDto {
  id: string;
  lane: string;
  title: string;
  startDate: string;
  endDate: string;
  color?: string;
  progressPercent?: number;
  key?: string;
  projectId?: string;
  projectName?: string;
  itemType?: string;
  status?: string;
}

/** Backend dependency shape (camelCase). */
export interface TimelineDependencyDto {
  fromItemId?: string;
  toItemId?: string;
  type?: string;
  fromId?: string;
  toId?: string;
  linkType?: string;
}

/** Backend bar shape for project timeline. */
export interface TimelineBarDto {
  id: string;
  itemType: string;
  title: string;
  subtitle?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
}

export interface ProjectTimelineDto {
  projectId: string;
  zoom?: string;
  /** UI-normalized */
  rangeStart?: string;
  rangeEnd?: string;
  items?: TimelineItemDto[];
  /** Backend wire format */
  from?: string;
  to?: string;
  bars?: TimelineBarDto[];
  dependencies?: TimelineDependencyDto[];
  criticalPath?: string[];
}

export interface SharedRoadmapDto extends ProjectTimelineDto {
  projectName?: string;
  token?: string;
  expiresAt?: string;
}

export interface ShareRoadmapResultDto {
  token: string;
  shareUrl?: string;
  expiresAt?: string;
}

export interface PortfolioSummaryDto {
  accountId: string;
  accountName: string;
  accountCode?: string;
  projectCount: number;
  totalIssues: number;
  overdueIssues: number;
  blockedIssues: number;
  completionPercent: number;
  healthScore: number;
  health: number;
  healthLabel: string;
}

export interface PortfolioRoadmapVersionDto {
  projectId: string;
  projectName: string;
  id: string;
  name: string;
  startDate?: string;
  releaseDate?: string;
  status: number;
  issueCount: number;
  completionPercent?: number;
  releaseNotes?: string;
}

export interface PortfolioRoadmapMilestoneDto {
  projectId: string;
  projectName: string;
  id: string;
  name: string;
  targetDate?: string;
  status: number;
  versionId?: string;
}

export interface PortfolioRoadmapQuarterDto {
  quarter: string;
  versions: PortfolioRoadmapVersionDto[];
  milestones: PortfolioRoadmapMilestoneDto[];
}

export interface PortfolioRoadmapDto {
  accountId: string;
  accountName: string;
  versions: PortfolioRoadmapVersionDto[];
  milestones: PortfolioRoadmapMilestoneDto[];
  quarters: PortfolioRoadmapQuarterDto[];
}

export interface PortfolioTimelineBarDto {
  id: string;
  projectId: string;
  projectName: string;
  itemType: string;
  title: string;
  subtitle?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
}

export interface PortfolioTimelineDto {
  accountId: string;
  accountName: string;
  zoom: string;
  from: string;
  to: string;
  bars: PortfolioTimelineBarDto[];
  dependencies?: TimelineDependencyDto[];
  criticalPath?: string[];
}
